import apiClient from "./api";
import type {
  Device,
  DeviceFilter,
  DeviceListResponse,
  DeviceFilterOptions,
  DeviceStats,
  DeviceBreakdownResponse,
  Command,
  CreateCommandRequest,
  DeviceLogFilter,
  DeviceLogListResponse,
  EnableLiveLoggingRequest,
  LiveLoggingResponse,
  EnableMemoryMonitorRequest,
  MemoryMonitorResponse,
  RebootRequest,
  RebootResponse,
  StorageInfoResponse,
  ShellCommandRequest,
  ShellCommandResponse,
  DeviceActionAuditsResponse,
  ListFilesystemResponse,
  GetItemSizeResponse,
  DeviceLogFileFilter,
  DeviceLogFilesResponse,
  DeviceLogFileStats,
  DeviceLogFileSyncResponse,
  SyncJobStatus,
  LogArchiveListResponse,
  LogArchiveDownloadURLResponse,
  DeviceSoftwareVersionsResponse,
} from "../types";
import { FirmwareOutdatedError } from "../types";
import { AxiosError } from "axios";

// Unwraps a 501 firmware_outdated response from the filesystem endpoints
// into a typed FirmwareOutdatedError so callers can render a dedicated
// "update firmware" state instead of a generic axios failure message.
function rethrowAsFirmwareOutdated(err: unknown): never {
  if (err instanceof AxiosError && err.response?.status === 501) {
    const body = err.response.data as
      | { code?: string; message?: string; minVersion?: string }
      | undefined;
    if (body?.code === "firmware_outdated") {
      throw new FirmwareOutdatedError(
        body.message ?? "Device firmware too old for filesystem analysis",
        body.minVersion ?? "1.5.0",
      );
    }
  }
  throw err;
}

/**
 * Facet params shared by `/devices` and `/devices/filter-options`.
 *
 * Both endpoints read these identically on the backend
 * (`deviceFacetFilterFromQuery`), so keeping one builder here is what makes the
 * options endpoint describe the same result set the list would return. Adding a
 * facet in one place and not the other is the drift this prevents.
 */
function facetParams(filter?: DeviceFilter): URLSearchParams {
  const params = new URLSearchParams();
  // Only honoured for super admins server-side; nobody sets it otherwise.
  if (filter?.tenant_id) params.append("tenant_id", filter.tenant_id);
  if (filter?.status) params.append("status", filter.status);
  // Multi-value filters → comma-separated. `.length` guard, not truthiness:
  // an empty array is truthy and would append an empty param.
  if (filter?.type?.length) params.append("type", filter.type.join(","));
  if (filter?.sub_type?.length)
    params.append("sub_type", filter.sub_type.join(","));
  if (filter?.model?.length) params.append("model", filter.model.join(","));
  if (filter?.os_version?.length)
    params.append("os_version", filter.os_version.join(","));
  if (filter?.agent_version?.length)
    params.append("agent_version", filter.agent_version.join(","));
  if (filter?.rom_version?.length)
    params.append("rom_version", filter.rom_version.join(","));
  // Ownership: internal vs customer. Only sent when set (the "All" case omits it).
  if (filter?.is_internal !== undefined)
    params.append("is_internal", String(filter.is_internal));
  if (filter?.last_seen) params.append("last_seen", filter.last_seen);
  // Only ever sent as "true". Omitting it is the "all machines" case, and the
  // backend rejects anything that is not true/false.
  if (filter?.favorite) params.append("favorite", "true");
  if (filter?.flag?.length) params.append("flag", filter.flag.join(","));
  if (filter?.search) params.append("search", filter.search);
  return params;
}

export const deviceService = {
  // List devices with optional filters
  list: async (filter?: DeviceFilter): Promise<DeviceListResponse> => {
    const params = facetParams(filter);
    if (filter?.group_id) params.append("group_id", filter.group_id);
    if (filter?.sort) params.append("sort", filter.sort);
    if (filter?.limit) params.append("limit", filter.limit.toString());
    if (filter?.offset) params.append("offset", filter.offset.toString());

    const queryString = params.toString();
    const url = `/devices${queryString ? `?${queryString}` : ""}`;
    return apiClient.get<DeviceListResponse>(url);
  },

  /**
   * Distinct values still reachable under `filter`. Pass the page's current
   * facet filter and the menu stops offering values that would return an empty
   * table. Omit it for the tenant-wide list.
   *
   * Send only facet params: `limit`/`offset`/`sort` don't change which values
   * exist, and including them would fragment the cache per page.
   */
  getFilterOptions: async (filter?: DeviceFilter): Promise<DeviceFilterOptions> => {
    const queryString = facetParams(filter).toString();
    return apiClient.get<DeviceFilterOptions>(
      `/devices/filter-options${queryString ? `?${queryString}` : ""}`,
    );
  },

  // Get device fleet stats (total, online, offline, pending, blocked)
  /**
   * Per-status fleet counts under `filter`, so the status tabs describe the
   * same result set the list does.
   *
   * Pass the facet filter WITHOUT `status`: the backend skips it anyway (the
   * tabs are the status selector), and omitting it keeps one cache entry
   * across tab switches instead of one per tab.
   */
  getStats: async (filter?: DeviceFilter): Promise<DeviceStats> => {
    const queryString = facetParams(filter).toString();
    return apiClient.get<DeviceStats>(
      `/devices/stats${queryString ? `?${queryString}` : ""}`,
    );
  },

  /**
   * Device counts across two dimensions at once.
   *
   * One GROUP BY server-side. Assembling this from getFilterOptions would take
   * a request per row value, each running the full facet UNION.
   */
  getBreakdown: async (
    rows: string,
    cols: string,
    filter?: DeviceFilter,
  ): Promise<DeviceBreakdownResponse> => {
    const params = facetParams(filter);
    params.set("rows", rows);
    params.set("cols", cols);
    return apiClient.get<DeviceBreakdownResponse>(`/devices/breakdown?${params.toString()}`);
  },

  // Get single device by ID
  get: async (id: string): Promise<Device> => {
    return apiClient.get<Device>(`/devices/${id}`);
  },

  // Delete device
  delete: async (id: string): Promise<void> => {
    return apiClient.delete(`/devices/${id}`);
  },

  // Set device ownership (super-admin-only). Backend echoes only { id, is_internal }.
  setInternal: async (
    id: string,
    isInternal: boolean,
  ): Promise<{ id: string; is_internal: boolean }> => {
    return apiClient.patch<{ id: string; is_internal: boolean }>(
      `/devices/${id}/internal`,
      { is_internal: isInternal },
    );
  },

  // Send command to device
  sendCommand: async (
    deviceId: string,
    command: CreateCommandRequest,
  ): Promise<Command> => {
    return apiClient.post<Command>(`/devices/${deviceId}/commands`, command);
  },

  // Get device commands
  getCommands: async (deviceId: string): Promise<Command[]> => {
    const response = await apiClient.get<{ commands: Command[] }>(
      `/devices/${deviceId}/commands`,
    );
    return response.commands || [];
  },

  getAllowedCommands: async (
    deviceId: string,
  ): Promise<{ type: string; allowed: boolean; danger: boolean }[]> => {
    const response = await apiClient.get<{
      commands: { type: string; allowed: boolean; danger: boolean }[];
    }>(`/devices/${deviceId}/allowed-commands`);
    return response.commands || [];
  },

  // Get device logs
  getLogs: async (
    deviceId: string,
    filter?: DeviceLogFilter,
  ): Promise<DeviceLogListResponse> => {
    const params = new URLSearchParams();
    if (filter?.log_type) params.append("log_type", filter.log_type);
    if (filter?.level) params.append("level", filter.level);
    if (filter?.search) params.append("search", filter.search);
    if (filter?.limit) params.append("limit", filter.limit.toString());
    if (filter?.offset) params.append("offset", filter.offset.toString());

    const queryString = params.toString();
    const url = `/devices/${deviceId}/logs${queryString ? `?${queryString}` : ""}`;
    return apiClient.get<DeviceLogListResponse>(url);
  },

  // Enable live logging on device
  // Uses noWait=true to avoid 30s timeout when device doesn't respond properly
  enableLiveLogging: async (
    deviceId: string,
    options?: EnableLiveLoggingRequest,
  ): Promise<LiveLoggingResponse> => {
    return apiClient.post<LiveLoggingResponse>(
      `/devices/${deviceId}/live-logging/enable?noWait=true`,
      options || { level: "info" },
    );
  },

  // Disable live logging on device
  // Uses noWait=true to avoid timeout issues
  disableLiveLogging: async (
    deviceId: string,
    reason: string = "user_stopped",
  ): Promise<LiveLoggingResponse> => {
    return apiClient.post<LiveLoggingResponse>(
      `/devices/${deviceId}/live-logging/disable?noWait=true`,
      { reason },
    );
  },

  // Enable activity monitor on device
  enableActivityMonitor: async (
    deviceId: string,
  ): Promise<LiveLoggingResponse> => {
    return apiClient.post<LiveLoggingResponse>(
      `/devices/${deviceId}/activity-monitor/enable?noWait=true`,
      {},
    );
  },

  // Disable activity monitor on device
  disableActivityMonitor: async (
    deviceId: string,
  ): Promise<LiveLoggingResponse> => {
    return apiClient.post<LiveLoggingResponse>(
      `/devices/${deviceId}/activity-monitor/disable?noWait=true`,
      {},
    );
  },

  // Enable memory monitor on device
  enableMemoryMonitor: async (
    deviceId: string,
    options?: EnableMemoryMonitorRequest,
  ): Promise<MemoryMonitorResponse> => {
    return apiClient.post<MemoryMonitorResponse>(
      `/devices/${deviceId}/memory-monitor/enable?noWait=true`,
      options || {},
    );
  },

  // Disable memory monitor on device
  disableMemoryMonitor: async (
    deviceId: string,
  ): Promise<MemoryMonitorResponse> => {
    return apiClient.post<MemoryMonitorResponse>(
      `/devices/${deviceId}/memory-monitor/disable?noWait=true`,
      {},
    );
  },

  // Send heartbeat to extend memory monitor session
  memoryMonitorHeartbeat: async (
    deviceId: string,
  ): Promise<{ success: boolean }> => {
    return apiClient.post<{ success: boolean }>(
      `/devices/${deviceId}/memory-monitor/heartbeat`,
      {},
    );
  },

  // Get memory monitor status
  getMemoryMonitorStatus: async (
    deviceId: string,
  ): Promise<{
    active: boolean;
    sessionId?: string;
    enabledAt?: string;
    expiresAt?: string;
    config?: { intervalMs: number; threshold: number };
  }> => {
    return apiClient.get(`/devices/${deviceId}/memory-monitor/status`);
  },

  // Factory reset device via MDM Protocol (fire-and-forget, requires online device).
  // The device wipes itself and never returns a MethodResponse, so the backend
  // publishes once and returns 202 immediately — no timeout race.
  factoryReset: async (
    deviceId: string,
    needUserAccept: boolean = true,
  ): Promise<{ success: boolean; deviceId: string; status: string }> => {
    needUserAccept = false;
    return apiClient.post<{
      success: boolean;
      deviceId: string;
      status: string;
    }>(
      `/devices/${deviceId}/factory-reset`,
      { needUserAccept },
    );
  },

  // Get device info directly from device via MDM Protocol (requires online device).
  // MDM agent version is exposed separately through getSoftwareVersions.
  getDeviceInfo: async (
    deviceId: string,
  ): Promise<{
    success: boolean;
    deviceId: string;
    response?: DeviceInfoResponse;
  }> => {
    return apiClient.get<{
      success: boolean;
      deviceId: string;
      response?: DeviceInfoResponse;
    }>(`/devices/${deviceId}/device-info`);
  },

  // Unified software inventory snapshot (MDM Agent + app/os/fw rows).
  // Backend-stored only; no refresh param — the UI never triggers a device
  // refresh for this list.
  getSoftwareVersions: async (
    deviceId: string,
  ): Promise<DeviceSoftwareVersionsResponse> => {
    return apiClient.get<DeviceSoftwareVersionsResponse>(
      `/devices/${deviceId}/software-versions`,
    );
  },

  // Diagnostic v1.1 methods
  // Use a 45s HTTP timeout so the backend's 30s MDM-call timeout has room to
  // return its specific "request expired" 500 instead of racing the axios
  // default 30s and surfacing a generic "timeout of 30000ms exceeded".
  reboot: async (
    deviceId: string,
    req?: RebootRequest,
  ): Promise<RebootResponse> => {
    return apiClient.post<RebootResponse>(
      `/devices/${deviceId}/reboot`,
      req || {},
      { timeout: 45000 },
    );
  },

  getStorageInfo: async (deviceId: string): Promise<StorageInfoResponse> => {
    return apiClient.get<StorageInfoResponse>(
      `/devices/${deviceId}/storage-info`,
    );
  },

  executeShellCommand: async (
    deviceId: string,
    req: ShellCommandRequest,
  ): Promise<ShellCommandResponse> => {
    // Use command timeout + 10s buffer for the HTTP request timeout
    const httpTimeout = (req.timeoutMs || 30000) + 10000;
    return apiClient.post<ShellCommandResponse>(
      `/devices/${deviceId}/shell`,
      req,
      { timeout: httpTimeout },
    );
  },

  // Diagnostic v1.5 filesystem listing — Disk Usage Analyzer.
  // Returns names + isDirectory only; sizes are fetched per row via
  // getItemSize so a slow recursive walk on /system can't drag down the
  // whole listing. List should always complete in ~one MQTT RTT.
  listFilesystem: async (
    deviceId: string,
    path: string,
  ): Promise<ListFilesystemResponse> => {
    try {
      return await apiClient.post<ListFilesystemResponse>(
        `/devices/${deviceId}/filesystem/list`,
        { path },
        { timeout: 25000 },
      );
    } catch (err) {
      rethrowAsFirmwareOutdated(err);
    }
  },

  // Recursive size of a single path. Each row in the analyzer fires one of
  // these after the listing arrives; the browser's HTTP/1.1 conn limit caps
  // concurrency naturally. 65s timeout sits above the backend's 60s
  // per-item ceiling so the server's specific error always reaches us.
  getItemSize: async (
    deviceId: string,
    path: string,
    isDirectory: boolean,
  ): Promise<GetItemSizeResponse> => {
    try {
      return await apiClient.post<GetItemSizeResponse>(
        `/devices/${deviceId}/filesystem/size`,
        { path, isDirectory },
        { timeout: 65000 },
      );
    } catch (err) {
      rethrowAsFirmwareOutdated(err);
    }
  },

  // Unified per-device action audit log. action filter narrows to a
  // single category (e.g. shell.execute, device.reboot). The shell
  // executor passes "shell.execute" to scope itself; the broader Audit
  // tab passes no filter to see everything.
  listActionAudits: async (
    deviceId: string,
    opts: { limit?: number; offset?: number; action?: string } = {},
  ): Promise<DeviceActionAuditsResponse> => {
    const params = new URLSearchParams();
    if (opts.limit) params.set("limit", String(opts.limit));
    if (opts.offset !== undefined) params.set("offset", String(opts.offset));
    if (opts.action) params.set("action", opts.action);
    const qs = params.toString();
    return apiClient.get<DeviceActionAuditsResponse>(
      `/devices/${deviceId}/action-audits${qs ? `?${qs}` : ""}`,
    );
  },

  // Log file management (admin-only)
  forceUploadLog: async (
    deviceId: string,
  ): Promise<{ success: boolean; message: string }> => {
    return apiClient.post<{ success: boolean; message: string }>(
      `/devices/${deviceId}/log-files/force-upload?noWait=true`,
      {},
    );
  },

  listLogFiles: async (
    deviceId: string,
    filter?: DeviceLogFileFilter,
  ): Promise<DeviceLogFilesResponse> => {
    const params = new URLSearchParams();
    if (filter?.search) params.append("search", filter.search);
    if (filter?.logType) params.append("logType", filter.logType);
    if (filter?.from) params.append("from", filter.from);
    if (filter?.to) params.append("to", filter.to);
    if (filter?.limit) params.append("limit", filter.limit.toString());
    if (filter?.offset) params.append("offset", filter.offset.toString());
    const qs = params.toString();
    return apiClient.get<DeviceLogFilesResponse>(
      `/devices/${deviceId}/log-files${qs ? `?${qs}` : ""}`,
    );
  },

  getLogFileStorageInfo: async (
    deviceId: string,
  ): Promise<DeviceLogFileStats> => {
    return apiClient.get<DeviceLogFileStats>(
      `/devices/${deviceId}/log-files/storage-info`,
    );
  },

  syncLogFilesFromGCS: async (
    deviceId: string,
  ): Promise<DeviceLogFileSyncResponse> => {
    return apiClient.post<DeviceLogFileSyncResponse>(
      `/devices/${deviceId}/log-files/sync-gcs`,
      {},
    );
  },

  getSyncGCSStatus: async (deviceId: string): Promise<SyncJobStatus> => {
    return apiClient.get<SyncJobStatus>(
      `/devices/${deviceId}/log-files/sync-gcs/status`,
    );
  },

  // Log archive (GCS-scan path) — coexists with the DB-tracked log-files endpoints above.
  listLogArchive: async (
    deviceId: string,
    from?: string,
    to?: string,
  ): Promise<LogArchiveListResponse> => {
    const params = new URLSearchParams();
    if (from) params.append("from", from);
    if (to) params.append("to", to);
    const qs = params.toString();
    return apiClient.get<LogArchiveListResponse>(
      `/devices/${deviceId}/log-archive${qs ? `?${qs}` : ""}`,
    );
  },

  getLogArchiveDownloadURL: async (
    deviceId: string,
    bucket: string,
    name: string,
  ): Promise<LogArchiveDownloadURLResponse> => {
    const params = new URLSearchParams({ bucket, name });
    return apiClient.get<LogArchiveDownloadURLResponse>(
      `/devices/${deviceId}/log-archive/download?${params.toString()}`,
    );
  },
};

// DeviceInfo response from MDM Protocol
export interface DeviceInfoResponse {
  deviceInfo?: {
    deviceId?: string;
    deviceType?: string;
    productVersion?: string;
    serialNumber?: string;
    hardwareVersion?: string;
    firmwareVersion?: string;
    deviceModel?: string;
    manufacturer?: string;
    androidVersion?: string;
    agentVersion?: string;
    romVersion?: string;
  };
}

// Shape returned by the DeviceInfo.getMDMVersion method. All fields
// optional because device firmware older than v1.4 doesn't implement
// the method — the backend swallows the METHOD_NOT_FOUND error and
// returns an empty object so the UI can render "—".
export interface MDMVersionResponse {
  version?: string;
  fullVersion?: string;
  versionCode?: number;
  buildDate?: string;
  gitCommit?: string;
  gitDirty?: boolean;
}

export default deviceService;
